// HTTP + SSE client for one device token against a DigiCalls backend.

const FALLBACK_ICE: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];

export type BrokerEvent = {
  type: string;
  sessionId?: string;
  id?: string;
  peer?: string;
  status?: string;
  reason?: string;
  [k: string]: unknown;
};

export class Client {
  readonly clientId: string;
  private iceCache: RTCIceServer[] | null = null;

  constructor(
    readonly baseUrl: string,
    readonly token: string,
    clientId?: string,
  ) {
    this.baseUrl = baseUrl.replace(/\/+$/, "");
    this.clientId = clientId ?? "dc_" + Math.random().toString(36).slice(2, 12);
  }

  private headers(): Record<string, string> {
    return {
      "Content-Type": "application/json",
      Authorization: "Bearer " + this.token,
      "X-Client-Id": this.clientId,
    };
  }

  async get<T>(path: string): Promise<T> {
    const r = await fetch(this.baseUrl + path, { headers: this.headers() });
    if (!r.ok) throw new Error(`${path} ${r.status}`);
    return (await r.json()) as T;
  }

  async post<T>(path: string, body: unknown = {}): Promise<T> {
    const r = await fetch(this.baseUrl + path, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(`${path} ${r.status}`);
    if (r.status === 204) return undefined as T;
    return (await r.json()) as T;
  }

  async del(path: string): Promise<void> {
    const r = await fetch(this.baseUrl + path, { method: "DELETE", headers: this.headers() });
    if (!r.ok && r.status !== 204) throw new Error(`${path} ${r.status}`);
  }

  // events opens the SSE stream for this device token. EventSource can't send
  // headers, so the token goes in the query (the backend reads ?token=).
  events(onEvent: (ev: BrokerEvent) => void): EventSource {
    const u = new URL(this.baseUrl + "/api/events");
    u.searchParams.set("token", this.token);
    u.searchParams.set("clientId", this.clientId);
    const es = new EventSource(u.toString());
    es.onmessage = (e) => {
      try {
        onEvent(JSON.parse(e.data));
      } catch {
        /* ignore keepalives / malformed */
      }
    };
    return es;
  }

  async iceServers(): Promise<RTCIceServer[]> {
    if (this.iceCache) return this.iceCache;
    try {
      const { iceServers } = await this.get<{ iceServers: RTCIceServer[] }>("/api/webrtc-config");
      this.iceCache = iceServers?.length ? iceServers : FALLBACK_ICE;
    } catch {
      this.iceCache = FALLBACK_ICE;
    }
    return this.iceCache;
  }
}
